/**
 * @fileoverview 休息時間コンプライアンス画面
 * @description 海事労働条約(MLC2006)の休息時間基準に対する乗船中船員の
 *              適合状況を期間指定で一覧表示し、監査提出用PDF（休息時間管理簿）を出力する。
 *              ソート・ステータス絞り込み・ページングに対応。判定基準は登録マスタ→休憩管理で調整できる。
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Settings } from 'lucide-react';
import { get, getToken } from '../services/api';
import { useSort, usePager, SortableTh } from '../components/TableTools';

interface RestRow {
  user_id: string;
  user_name: string;
  employee_code: string;
  vessel_id: string;
  vessel_name: string;
  rest_24h_total_min: number;
  rest_24h_max_continuous_min: number;
  violation_24h: boolean;
  rest_7d_total_min: number;
  violation_7d: boolean;
  work_24h_min: number;
  violation_days: number;
  warning_days: number;
  status: 'ok' | 'warning' | 'violation';
}

// MLC 2006 基準（分）: 表示上の赤字判定に使用
const MIN_CONTINUOUS_24H = 10 * 60;
const MIN_TOTAL_7D       = 77 * 60;

const fmtH = (m: number) => {
  if (m == null || isNaN(m)) return '-';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}時間${mm}分` : `${h}時間`;
};

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ─── 期間プリセット ──────────────────────────────────────────────────────────
const PRESETS: { key: string; label: string; range: () => [string, string] }[] = [
  { key: 'today', label: '今日', range: () => { const t = ymd(new Date()); return [t, t]; } },
  { key: 'this-week', label: '今週', range: () => {
    const now = new Date(); const dow = (now.getDay() + 6) % 7; // 月曜始まり
    const mon = new Date(now); mon.setDate(now.getDate() - dow);
    return [ymd(mon), ymd(now)];
  } },
  { key: 'last-week', label: '先週', range: () => {
    const now = new Date(); const dow = (now.getDay() + 6) % 7;
    const mon = new Date(now); mon.setDate(now.getDate() - dow - 7);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return [ymd(mon), ymd(sun)];
  } },
  { key: 'this-month', label: '今月', range: () => {
    const now = new Date();
    return [ymd(new Date(now.getFullYear(), now.getMonth(), 1)), ymd(now)];
  } },
  { key: 'last-month', label: '先月', range: () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last  = new Date(now.getFullYear(), now.getMonth(), 0);
    return [ymd(first), ymd(last)];
  } },
];

const STATUS_META: Record<RestRow['status'], { label: string; color: string; bg: string; border: string }> = {
  violation: { label: '違反', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  warning:   { label: '警告', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  ok:        { label: '適合', color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
};

function StatusBadge({ status }: { status: RestRow['status'] }) {
  const m = STATUS_META[status] ?? STATUS_META.ok;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
    }}>{m.label}</span>
  );
}

const STATUS_RANK: Record<RestRow['status'], number> = { violation: 0, warning: 1, ok: 2 };

const RestHoursPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState<RestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom]       = useState(() => ymd(new Date()));
  const [to, setTo]           = useState(() => ymd(new Date()));
  const [preset, setPreset]   = useState('today');
  const [statusFilter, setStatusFilter] = useState<RestRow['status'] | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const multiDay = from !== to;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get<any>('/rest-hours', { from, to });
      setRows(res?.data ?? []);
    } catch (e: any) {
      console.error('[RestHoursPage]', e);
      setRows([]);
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (key: string) => {
    const p = PRESETS.find(p => p.key === key);
    if (!p) return;
    const [f, t] = p.range();
    setPreset(key); setFrom(f); setTo(t);
  };

  // 絞り込み → ソート → ページング
  const filtered = useMemo(
    () => (statusFilter ? rows.filter(r => r.status === statusFilter) : rows),
    [rows, statusFilter],
  );
  const sort = useSort<RestRow>(filtered, {
    employee_code:               r => r.employee_code,
    user_name:                   r => r.user_name,
    vessel_name:                 r => r.vessel_name,
    rest_24h_max_continuous_min: r => r.rest_24h_max_continuous_min,
    rest_24h_total_min:          r => r.rest_24h_total_min,
    rest_7d_total_min:           r => r.rest_7d_total_min,
    work_24h_min:                r => r.work_24h_min,
    violation_days:              r => r.violation_days,
    status:                      r => STATUS_RANK[r.status],
  }, 'employee_code');
  const pager = usePager(sort.sorted, { noun: '名' });
  const pageRows = pager.pageRows;

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const subdomain = (() => {
        const h = window.location.hostname;
        if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; }
        return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
      })();
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
      const token = getToken() ?? '';
      // 画面の絞り込み・ソート状態をそのままPDFに反映する
      const params = new URLSearchParams({ from, to, sort: sort.sortKey, dir: sort.sortDir });
      if (statusFilter) params.set('status', statusFilter);
      const r = await fetch(`${apiBase}/rest-hours/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' },
      });
      if (!r.ok) throw new Error('PDF生成に失敗しました');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `休息時間管理簿_${from}${multiDay ? `_${to}` : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      alert(e.message ?? 'PDF出力に失敗しました');
    }
    setPdfLoading(false);
  };

  const violationCount = rows.filter(r => r.status === 'violation').length;
  const warningCount   = rows.filter(r => r.status === 'warning').length;

  // クリックで絞り込むサマリーチップ
  const chip = (label: React.ReactNode, active: boolean, activeColors: { bg: string; border: string; color: string }, onClick: () => void) => (
    <button onClick={onClick} style={{
      background: active ? activeColors.bg : '#fff', borderRadius: 10, padding: '10px 18px',
      border: `2px solid ${active ? activeColors.color : '#e2e8f0'}`, fontSize: 13, cursor: 'pointer',
      color: activeColors.color, fontWeight: active ? 700 : 500,
    }}>{label}</button>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">休息時間</h1>
        <div className="flex-row">
          <button className="btn-o" onClick={() => navigate('/master/activities?tab=rest')}
            title="判定基準（最低休息・警告ライン）を登録マスタで調整します">
            <Settings size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            マスタ管理
          </button>
          <button className="btn-o" onClick={handlePdf} disabled={pdfLoading}
            style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}>
            <FileText size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            {pdfLoading ? '生成中…' : 'PDF出力'}
          </button>
        </div>
      </div>

      {/* 基準の説明カード */}
      <div className="card" style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <strong style={{ fontWeight: 700 }}>海事労働条約(MLC2006)基準:</strong>{' '}
          24時間で連続10時間以上の休息 / 7日間で77時間以上の休息
          {multiDay && <span style={{ marginLeft: 8, color: '#3b82f6' }}>※ 期間指定時は各日で判定した最悪値と違反日数を表示します</span>}
        </div>
      </div>

      {/* フィルタ（期間） */}
      <div className="flex-row" style={{ marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div className="form-g" style={{ margin: 0 }}>
          <label className="form-l">期間</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="form-i" type="date" value={from} max={to}
              onChange={e => { setFrom(e.target.value); setPreset(''); }} style={{ width: 160 }} />
            <span style={{ color: '#94a3b8' }}>〜</span>
            <input className="form-i" type="date" value={to} min={from}
              onChange={e => { setTo(e.target.value); setPreset(''); }} style={{ width: 160 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => applyPreset(p.key)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
              border: preset === p.key ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0',
              background: preset === p.key ? 'rgba(14,165,233,0.08)' : '#fff',
              color: preset === p.key ? '#0284c7' : '#475569',
              fontWeight: preset === p.key ? 700 : 500,
            }}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* サマリーバー（クリックで絞り込み） */}
      <div className="flex-row" style={{ marginBottom: 16 }}>
        {chip(<><span style={{ color: '#64748b' }}>対象人数: </span><strong>{rows.length}名</strong></>,
          statusFilter === null, { bg: '#f0f9ff', border: '#bae6fd', color: '#0369a1' }, () => setStatusFilter(null))}
        {chip(<>違反 {violationCount}名</>,
          statusFilter === 'violation', { bg: '#fef2f2', border: '#fecaca', color: violationCount > 0 ? '#dc2626' : '#94a3b8' }, () => setStatusFilter(f => f === 'violation' ? null : 'violation'))}
        {chip(<>警告 {warningCount}名</>,
          statusFilter === 'warning', { bg: '#fffbeb', border: '#fde68a', color: warningCount > 0 ? '#d97706' : '#94a3b8' }, () => setStatusFilter(f => f === 'warning' ? null : 'warning'))}
        {statusFilter && (
          <span style={{ alignSelf: 'center', fontSize: 12, color: '#64748b' }}>
            「{STATUS_META[statusFilter].label}」のみ表示中（{filtered.length}名）
          </span>
        )}
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <>
          {pager.renderBar('top')}
          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <SortableTh label="社員コード" k="employee_code" sort={sort} />
                  <SortableTh label="船員名" k="user_name" sort={sort} />
                  <SortableTh label="船舶" k="vessel_name" sort={sort} />
                  <SortableTh label="24h最長連続休息" k="rest_24h_max_continuous_min" sort={sort} />
                  <SortableTh label="24h休息合計" k="rest_24h_total_min" sort={sort} />
                  <SortableTh label="7日休息合計" k="rest_7d_total_min" sort={sort} />
                  <SortableTh label="勤務(24h)" k="work_24h_min" sort={sort} />
                  {multiDay && <SortableTh label="違反日数" k="violation_days" sort={sort} />}
                  <SortableTh label="ステータス" k="status" sort={sort} />
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={multiDay ? 9 : 8} className="empty">
                    {statusFilter ? `「${STATUS_META[statusFilter].label}」に該当する船員はいません` : '乗船中の船員がいません'}
                  </td></tr>
                ) : pageRows.map(r => {
                  const contShort = r.rest_24h_max_continuous_min < MIN_CONTINUOUS_24H;
                  const weekShort  = r.rest_7d_total_min < MIN_TOTAL_7D;
                  return (
                    <tr key={r.user_id} style={{ background: r.status === 'violation' ? '#fef2f2' : undefined }}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12.5 }}>{r.employee_code || '-'}</td>
                      <td style={{ fontWeight: 600 }}>{r.user_name}</td>
                      <td>{r.vessel_name || '-'}</td>
                      <td style={{ color: contShort ? '#dc2626' : undefined, fontWeight: contShort ? 700 : undefined }}>
                        {fmtH(r.rest_24h_max_continuous_min)}
                      </td>
                      <td>{fmtH(r.rest_24h_total_min)}</td>
                      <td style={{ color: weekShort ? '#dc2626' : undefined, fontWeight: weekShort ? 700 : undefined }}>
                        {fmtH(r.rest_7d_total_min)}
                      </td>
                      <td>{fmtH(r.work_24h_min)}</td>
                      {multiDay && (
                        <td style={{ color: r.violation_days > 0 ? '#dc2626' : undefined, fontWeight: r.violation_days > 0 ? 700 : undefined }}>
                          {r.violation_days > 0 ? `${r.violation_days}日` : '-'}
                        </td>
                      )}
                      <td><StatusBadge status={r.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pager.renderBar('bottom')}
        </>
      )}
    </div>
  );
};

export default RestHoursPage;
